// =====================================================================
// Motor de inferência de gráficos (especificação em docs/CHARTS_STUDY.md).
// Puro (sem React/Recharts): classifica colunas pela FORMA dos dados e sugere
// uma lista RANQUEADA de gráficos. Usado pelo back (sugestão no AgentAnswer) e
// pelo front (re-render + troca manual). Mesma lógica nos dois lados (DRY).
// =====================================================================

export type ColumnRole = "temporal" | "quantitative" | "categorical" | "identifier";

export interface ColumnProfile {
  name: string;
  role: ColumnRole;
  cardinality: number; // valores distintos não-nulos na amostra
  numericRatio: number; // fração dos não-nulos que são numéricos
  nullRatio: number;
  sampleSize: number;
}

export type ChartKind =
  | "line"
  | "area"
  | "bar"
  | "stackedBar"
  | "groupedBar"
  | "horizontalBar"
  | "pie"
  | "scatter"
  | "kpi"
  | "table";

export interface ChartSpec {
  kind: ChartKind;
  /** eixo de categoria/tempo (X). */
  x?: string;
  /** medidas quantitativas plotadas diretamente (quando não há groupBy). */
  series: string[];
  /** categórica que vira séries via pivot (stacked/grouped/multi-linha). */
  groupBy?: string;
  /** medida única usada quando há groupBy. */
  measure?: string;
  /** eixos para dispersão. */
  y?: string;
  /** rótulo de ponto (scatter) / ranking. */
  label?: string;
  title?: string;
  /** explicação curta — por que esse gráfico (explicabilidade na UI). */
  reason?: string;
  /** limite de categorias para ranking/pizza. */
  topN?: number;
}

const TOP_N = 15;
const PIE_MAX_SLICES = 6;
const SAMPLE = 200;

// ---- parsing tolerante (reaproveita a lógica pt-BR da planilha) ----

/** Converte valor para número aceitando string pt-BR ("1.234,56", "303,47"). */
export function toNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  let s = v.replace(/[^0-9,.\-]/g, "");
  if (s === "" || s === "-") return null;
  if (s.includes(",") && s.includes(".")) s = s.replace(/\./g, "").replace(",", ".");
  else if (s.includes(",")) s = s.replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Número ESTRITO para CLASSIFICAÇÃO (diferente de toNumber, que é tolerante p/
// render): "01_REGIAO"/"PLACA0" NÃO são números — só dígitos/sep./%/R$ valem.
const NUMERIC_RE = /^-?[\d.,]+%?$/;
function strictNumeric(v: unknown): boolean {
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v !== "string") return false;
  const s = v.trim().replace(/^r\$\s*/i, "").replace(/\s/g, "");
  return s !== "" && s !== "-" && NUMERIC_RE.test(s) && toNumber(s) != null;
}

const DATEISH = /^\d{4}-\d{2}(-\d{2})?([ t]\d{2}:\d{2})?/i;
function looksDate(v: unknown): boolean {
  if (v instanceof Date) return true;
  if (typeof v === "string") return DATEISH.test(v.trim());
  return false;
}

const TEMPORAL_NAME = /(competencia|compet[êe]ncia|data|date|m[êe]s|dia|periodo|per[íi]odo|validade|created|_at)$|^data|^mes/i;
const QUANT_NAME = /(peso|frete|tarifa|valor|qtd|quant|count|total|media|m[ée]dio|n_|num|dias|saldo|percent|pct|taxa)/i;
const ID_NAME = /(^id$|_id$|numero_ordem|n[uú]mero|cpf|cnpj|placa|nome|motorista|^email)/i;
const AVG_NAME = /(media|m[ée]di[oa]|avg|ticket)/i;
// Nomes "de dimensão" (poucos valores, boa série) — desempata cardinalidades iguais.
const DIM_NAME = /(status|tipo|severit|categoria|classe|flag|situa)/i;

function distinct(values: unknown[]): number {
  const set = new Set(values.map((v) => (v == null ? "" : String(v))));
  set.delete("");
  return set.size;
}

/**
 * Classifica cada coluna em temporal / quantitativa / categórica / identificador,
 * a partir de uma amostra das linhas (nome + conteúdo). Ver CHARTS_STUDY §3.
 */
export function classifyColumns(
  columns: string[],
  rows: Record<string, unknown>[],
): ColumnProfile[] {
  const sample = rows.slice(0, SAMPLE);
  const n = sample.length || 1;
  return columns.map((name) => {
    const vals = sample.map((r) => r[name]);
    const nonNull = vals.filter((v) => v != null && v !== "");
    const nullRatio = 1 - nonNull.length / n;
    const numeric = nonNull.filter(strictNumeric).length;
    const numericRatio = nonNull.length ? numeric / nonNull.length : 0;
    const dateRatio = nonNull.length ? nonNull.filter(looksDate).length / nonNull.length : 0;
    const card = distinct(vals);

    // Coluna 100% vazia: não é eixo nem medida — trata como rótulo inerte
    // (fica fora de quants/cats/temporais, não gera gráfico estranho).
    if (nonNull.length === 0) {
      return { name, role: "identifier", cardinality: 0, numericRatio: 0, nullRatio: 1, sampleSize: n };
    }

    let role: ColumnRole;
    if (TEMPORAL_NAME.test(name) || dateRatio >= 0.7) {
      role = "temporal";
    } else if (numericRatio >= 0.8 && !ID_NAME.test(name)) {
      role = "quantitative";
    } else if (ID_NAME.test(name)) {
      role = "identifier";
    } else if (card >= 20 && card >= nonNull.length * 0.9) {
      // cardinalidade alta E ≈ nº de linhas ⇒ identificador (rótulo, não agrupador).
      // O piso absoluto (20) evita rotular categóricas em amostras pequenas.
      role = "identifier";
    } else {
      role = "categorical";
    }
    // reforço por nome (resolve colunas ambíguas com poucos exemplos)
    if (role === "categorical" && QUANT_NAME.test(name) && numericRatio >= 0.5 && !ID_NAME.test(name)) {
      role = "quantitative";
    }
    return { name, role, cardinality: card, numericRatio, nullRatio, sampleSize: n };
  });
}

// Um temporal só serve de EIXO se tem poucos valores distintos (ex.: meses).
// Datas quase-únicas por linha (ex.: validade, muitos dias distintos) passam de
// 36 e caem fora — viram detalhe de tabela/ranking, não eixo.
function isAxisTemporal(p: ColumnProfile, rowCount: number, recordLevel: boolean): boolean {
  if (p.role !== "temporal" || p.cardinality > 36) return false;
  // Em dados em NÍVEL DE REGISTRO (há um identificador ~único por linha), uma data
  // quase-única por linha (ex.: validade, created_at) é ATRIBUTO do registro, não um
  // eixo de período → não vira eixo (senão vira barra empilhada no tempo ilegível,
  // com rótulos de timestamp sobrepostos). Ver simulação compliance-vencida.
  if (recordLevel && p.cardinality >= rowCount * 0.8) return false;
  return true;
}

// Ordena categóricas para escolher a SÉRIE (groupBy): menor cardinalidade
// primeiro; em empate, nomes "de dimensão" (status/tipo) antes (ver DIM_NAME).
function rankCats(cats: ColumnProfile[]): ColumnProfile[] {
  return [...cats].sort(
    (a, b) => a.cardinality - b.cardinality || (DIM_NAME.test(b.name) ? 1 : 0) - (DIM_NAME.test(a.name) ? 1 : 0),
  );
}

// Ranqueia medidas: totais/contagens antes de médias (eixo Y principal melhor).
function rankMeasures(quants: ColumnProfile[]): string[] {
  return [...quants]
    .sort((a, b) => (AVG_NAME.test(a.name) ? 1 : 0) - (AVG_NAME.test(b.name) ? 1 : 0))
    .map((q) => q.name);
}

/**
 * Sugere uma lista RANQUEADA de gráficos para o shape dos dados (1º = primário).
 * Implementa a árvore de decisão de CHARTS_STUDY §4. `table` é sempre a última
 * opção (fallback universal).
 */
export function suggestChartSpecs(
  columns: string[],
  rows: Record<string, unknown>[],
): ChartSpec[] {
  const profiles = classifyColumns(columns, rows);
  const rowCount = rows.length;
  const byName = (n: string) => profiles.find((p) => p.name === n)!;

  // Dados em NÍVEL DE REGISTRO: há um identificador (nome/placa/id) com ~1 valor por
  // linha. A intuição nesses casos é RANKING/tabela — não série temporal por atributo.
  const identifiers = profiles.filter((p) => p.role === "identifier" && p.cardinality > 1);
  const recordLevel = rowCount > 0 && identifiers.some((p) => p.cardinality >= rowCount * 0.8);

  const temporalAxes = profiles.filter((p) => isAxisTemporal(p, rowCount, recordLevel));
  const quants = profiles.filter((p) => p.role === "quantitative");
  const cats = rankCats(profiles.filter((p) => p.role === "categorical"));
  const catsLow = cats.filter((c) => c.cardinality <= 12);
  const idsOrHighCard = [
    ...profiles.filter((p) => p.role === "identifier"),
    ...cats.filter((c) => c.cardinality > 12),
  ];
  const measures = rankMeasures(quants);
  const specs: ChartSpec[] = [];

  const positiveMeasure = (m: string) =>
    rows.every((r) => {
      const v = toNumber(r[m]);
      return v == null || v >= 0;
    });

  if (measures.length === 0) {
    // Sem medida: distribuição = CONTAGEM por categoria. Oferece barra (padrão),
    // pizza (se poucas fatias) e ranking — todas alternam pela mesma agregação.
    if (catsLow.length) {
      const c = catsLow[0];
      specs.push({
        kind: "bar",
        x: c.name,
        series: ["__count__"],
        title: `Contagem por ${c.name}`,
        reason: "categórica sem medida numérica → contagem por categoria",
      });
      if (c.cardinality <= PIE_MAX_SLICES) {
        specs.push({ kind: "pie", x: c.name, series: [], measure: "__count__", topN: PIE_MAX_SLICES, reason: "parte-do-todo (contagem)" });
      }
      specs.push({ kind: "horizontalBar", x: c.name, series: ["__count__"], topN: TOP_N, reason: "ranking por contagem" });
    }
  } else if (temporalAxes.length) {
    const t = temporalAxes[0].name;
    if (catsLow.length) {
      const g = catsLow[0].name; // menor cardinalidade = melhor série
      const m = measures[0];
      specs.push({
        kind: "stackedBar",
        x: t,
        series: [],
        groupBy: g,
        measure: m,
        title: `${m} por ${t} (empilhado por ${g})`,
        reason: "tempo × categoria de baixa cardinalidade → composição no tempo",
      });
      specs.push({ kind: "groupedBar", x: t, series: [], groupBy: g, measure: m, reason: "comparação lado a lado por período" });
      specs.push({ kind: "line", x: t, series: [], groupBy: g, measure: m, reason: "tendência de cada categoria" });
      specs.push({ kind: "area", x: t, series: [], groupBy: g, measure: m, reason: "volume acumulado no tempo" });
    } else {
      specs.push({
        kind: "line",
        x: t,
        series: measures,
        title: `Evolução de ${measures.join(", ")} por ${t}`,
        reason: "1 eixo temporal × medidas → tendência",
      });
      specs.push({ kind: "area", x: t, series: measures, reason: "volume no tempo" });
      specs.push({ kind: "bar", x: t, series: measures, reason: "comparação por período" });
    }
  } else if (catsLow.length >= 2) {
    // Duas categóricas: x = a de MAIOR cardinalidade, série = a de MENOR.
    const g = catsLow[0].name; // menor
    const x = catsLow[1].name; // maior (entre as duas menores)
    const m = measures[0];
    specs.push({
      kind: "groupedBar",
      x,
      series: [],
      groupBy: g,
      measure: m,
      title: `${m} por ${x} e ${g}`,
      reason: "duas categóricas × medida → barras agrupadas",
    });
    specs.push({ kind: "stackedBar", x, series: [], groupBy: g, measure: m, reason: "composição por categoria" });
    if (byName(x).cardinality <= PIE_MAX_SLICES && positiveMeasure(m)) {
      specs.push({ kind: "pie", x, series: [], measure: m, topN: PIE_MAX_SLICES, reason: "parte-do-todo (poucas fatias)" });
    }
  } else {
    // Sem tempo, ≤ 1 categórica de baixa cardinalidade.
    const m = measures[0];
    if (idsOrHighCard.length) {
      // Dados em nível de registro / muitas categorias → ranking top-N.
      const label = idsOrHighCard[0].name;
      specs.push({
        kind: "horizontalBar",
        x: label,
        series: [m],
        topN: TOP_N,
        title: `Top ${TOP_N} por ${m}`,
        reason: "categórica de alta cardinalidade → ranking (top-N)",
      });
    }
    if (catsLow.length === 1) {
      const c = catsLow[0].name;
      specs.push({
        kind: "bar",
        x: c,
        series: [m],
        title: `${m} por ${c}`,
        reason: "categoria × medida → barras",
      });
      if (byName(c).cardinality <= PIE_MAX_SLICES && positiveMeasure(m)) {
        specs.push({ kind: "pie", x: c, series: [], measure: m, topN: PIE_MAX_SLICES, reason: "parte-do-todo" });
      }
    }
    if (measures.length >= 2) {
      specs.push({
        kind: "scatter",
        x: measures[0],
        y: measures[1],
        series: [],
        label: idsOrHighCard[0]?.name ?? catsLow[0]?.name,
        title: `${measures[0]} × ${measures[1]}`,
        reason: "duas medidas → correlação (dispersão)",
      });
    }
  }

  // KPI quando há uma única linha com medida (valor único).
  if (rowCount === 1 && measures.length) {
    specs.unshift({ kind: "kpi", series: measures, reason: "valor único → indicador" });
  }

  // Tabela é sempre a última alternativa.
  specs.push({ kind: "table", series: [], reason: "detalhe / auditoria" });

  // Dedupe por (kind,x,groupBy,measure).
  const seen = new Set<string>();
  return specs.filter((s) => {
    const k = `${s.kind}|${s.x ?? ""}|${s.groupBy ?? ""}|${s.measure ?? ""}|${s.series.join(",")}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Atalho: o gráfico primário (1º da lista, ignorando a tabela se houver melhor). */
export function primaryChart(columns: string[], rows: Record<string, unknown>[]): ChartSpec {
  return suggestChartSpecs(columns, rows)[0];
}

// ---- Preparação de dados para renderização (pivot/agg/top-N) ----

export interface ChartData {
  rows: Record<string, unknown>[];
  seriesKeys: string[];
  xKey?: string;
  truncated?: { shown: number; total: number };
}

const COUNT = "__count__";

/**
 * Transforma as linhas brutas no formato pronto para o gráfico do `spec`:
 * pivota dados longos (groupBy → colunas), agrega contagens, ordena ranking e
 * aplica top-N. Mantém a lógica fora do componente (testável, reaproveitável).
 */
export function buildChartData(spec: ChartSpec, rows: Record<string, unknown>[]): ChartData {
  const num = (v: unknown) => toNumber(v) ?? 0;

  // --- Pizza: agrega medida por categoria, top-N + "Outros" ---
  if (spec.kind === "pie") {
    const key = spec.x!;
    const m = spec.measure ?? spec.series[0];
    const agg = new Map<string, number>();
    for (const r of rows) {
      const k = String(r[key] ?? "—");
      // Pizza é parte-do-todo: valor negativo não tem fatia → clamp em 0.
      const v = m === COUNT ? 1 : Math.max(0, num(r[m]));
      agg.set(k, (agg.get(k) ?? 0) + v);
    }
    let entries = [...agg.entries()].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
    const total = entries.length;
    const n = Math.max(1, spec.topN ?? PIE_MAX_SLICES);
    if (entries.length > n) {
      const head = entries.slice(0, n - 1);
      const rest = entries.slice(n - 1).reduce((s, e) => s + e[1], 0);
      entries = [...head, ["Outros", rest]];
    }
    return {
      rows: entries.map(([name, value]) => ({ name, value })),
      seriesKeys: ["value"],
      xKey: "name",
      truncated: total > n ? { shown: n, total } : undefined,
    };
  }

  // --- Dispersão: pares (x,y) numéricos ---
  if (spec.kind === "scatter") {
    return {
      rows: rows.map((r) => ({ x: num(r[spec.x!]), y: num(r[spec.y!]), name: spec.label ? r[spec.label] : undefined })),
      seriesKeys: ["y"],
      xKey: "x",
    };
  }

  // --- KPI: passa as linhas como estão ---
  if (spec.kind === "kpi" || spec.kind === "table") {
    return { rows, seriesKeys: spec.series };
  }

  // --- Pivot (groupBy → colunas de série), somando a medida por (x, grupo) ---
  if (spec.groupBy && spec.x) {
    const m = spec.measure ?? spec.series[0] ?? COUNT; // fallback evita agregação muda
    const xKey = spec.x;
    const buckets = new Map<string, Record<string, number>>();
    const seriesTotals = new Map<string, number>();
    for (const r of rows) {
      const xv = String(r[xKey] ?? "—");
      const gv = String(r[spec.groupBy] ?? "—");
      const v = m === COUNT ? 1 : num(r[m]);
      const row = buckets.get(xv) ?? {};
      row[gv] = (row[gv] ?? 0) + v;
      buckets.set(xv, row);
      seriesTotals.set(gv, (seriesTotals.get(gv) ?? 0) + v);
    }
    // top-N séries por total (evita legendas gigantes); resto vira "Outros".
    let keys = [...seriesTotals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
    const totalKeys = keys.length;
    const limit = Math.max(1, spec.topN ?? 8);
    let rest: string[] = [];
    if (keys.length > limit) {
      rest = keys.slice(limit);
      keys = keys.slice(0, limit);
    }
    // Eixo num campo RESERVADO se algum valor de série colidir com o nome da
    // coluna do X (ex.: status === "competencia") — senão out[k] sobrescreveria
    // o rótulo do eixo com um número.
    const collides = keys.includes(xKey) || (rest.length > 0 && xKey === "Outros");
    const axisKey = collides ? `__x_${xKey}` : xKey;
    const data = [...buckets.entries()].map(([xv, row]) => {
      const out: Record<string, unknown> = { [axisKey]: xv };
      for (const k of keys) out[k] = row[k] ?? 0;
      if (rest.length) out["Outros"] = rest.reduce((s, k) => s + (row[k] ?? 0), 0);
      return out;
    });
    return {
      rows: data,
      seriesKeys: rest.length ? [...keys, "Outros"] : keys,
      xKey: axisKey,
      truncated: totalKeys > limit ? { shown: limit, total: totalKeys } : undefined,
    };
  }

  // --- Contagem por categoria (bar/line/area/horizontalBar com medida __count__,
  // sem groupBy): AGREGA nº de registros por valor de X. Sem isto, um "bar __count__"
  // cairia nas séries diretas e plotaria 1 barra de altura 1 por linha (achado da
  // simulação: 30 barras idênticas em inconsistências). Ordena desc + top-N.
  if (spec.x && !spec.groupBy && spec.series.includes(COUNT)) {
    const agg = new Map<string, number>();
    for (const r of rows) {
      const k = String(r[spec.x] ?? "—");
      agg.set(k, (agg.get(k) ?? 0) + 1);
    }
    let entries = [...agg.entries()].sort((a, b) => b[1] - a[1]);
    const total = entries.length;
    const n = Math.max(1, spec.topN ?? TOP_N);
    const truncated = total > n ? { shown: n, total } : undefined;
    if (total > n) entries = entries.slice(0, n);
    return {
      rows: entries.map(([k, v]) => ({ [spec.x!]: k, [COUNT]: v })),
      seriesKeys: [COUNT],
      xKey: spec.x,
      truncated,
    };
  }

  // --- Ranking horizontal: ordena por MAGNITUDE da 1ª medida (desc), top-N.
  // Magnitude (e não valor) coloca os maiores em módulo no topo — p/ medidas
  // negativas (ex.: dias_faltantes = atraso) mostra os MAIS vencidos primeiro.
  if (spec.kind === "horizontalBar") {
    const m = spec.series[0];
    const sorted = [...rows]
      .map((r) => ({ ...r, [m]: m === COUNT ? 1 : num(r[m]) }))
      .sort((a, b) => Math.abs(num(b[m])) - Math.abs(num(a[m])));
    const n = Math.max(1, spec.topN ?? TOP_N);
    return {
      rows: sorted.slice(0, n),
      seriesKeys: [m],
      xKey: spec.x,
      truncated: sorted.length > n ? { shown: n, total: sorted.length } : undefined,
    };
  }

  // --- Séries diretas (linha/área/barra sem groupBy): coage medidas a número ---
  const seriesKeys = spec.series.includes(COUNT) ? ["__count__"] : spec.series;
  const data = rows.map((r) => {
    const out: Record<string, unknown> = { ...r };
    for (const k of seriesKeys) out[k] = k === COUNT ? 1 : num(r[k]);
    return out;
  });
  return { rows: data, seriesKeys, xKey: spec.x };
}

// =====================================================================
// Customização do usuário (controles em tempo real, persistidos por usuário).
// =====================================================================

export interface ChartCustomization {
  /** troca o tipo de gráfico (sobrepõe o sugerido). */
  kind?: ChartKind;
  /** domínio do eixo Y (null/undefined = automático). */
  yMin?: number | null;
  yMax?: number | null;
  /** reordena categorias/barras pelo total da medida. */
  sort?: "none" | "asc" | "desc";
  /** limita o nº de categorias (ranking/pizza/pivot). */
  topN?: number;
  /** barras: empilhado (true) vs. agrupado (false). */
  stacked?: boolean;
  /** séries ocultadas (toggle de legenda). */
  hiddenSeries?: string[];
}

/** Spec efetivo após a customização (kind, stacked→kind, topN). */
export function applyCustomization(spec: ChartSpec, c?: ChartCustomization): ChartSpec {
  if (!c) return spec;
  let kind = c.kind ?? spec.kind;
  if (c.stacked !== undefined && (kind === "stackedBar" || kind === "groupedBar")) {
    kind = c.stacked ? "stackedBar" : "groupedBar";
  }
  return { ...spec, kind, topN: c.topN ?? spec.topN };
}

/** Reordena as linhas já preparadas pelo total das séries (reordenar barras). */
export function sortChartData(
  data: ChartData,
  sort: "none" | "asc" | "desc" | undefined,
): ChartData {
  if (!sort || sort === "none") return data;
  const total = (r: Record<string, unknown>) =>
    data.seriesKeys.reduce((s, k) => s + (toNumber(r[k]) ?? 0), 0);
  const rows = [...data.rows].sort((a, b) => (sort === "asc" ? total(a) - total(b) : total(b) - total(a)));
  return { ...data, rows };
}

/** Achata séries ocultas: remove-as das seriesKeys (toggle de legenda). */
export function hideSeries(data: ChartData, hidden?: string[]): ChartData {
  if (!hidden?.length) return data;
  const set = new Set(hidden);
  return { ...data, seriesKeys: data.seriesKeys.filter((k) => !set.has(k)) };
}
